/**
 * useSysadminQuery — TanStack Query factory + hook for sysadmin scopes.
 *
 * Phase 5 PR-0. Per the architect's §1.3:
 *   - 10-second refresh interval (matches existing useSysadminServices
 *     / useRuntimeHardware cadence so every sysadmin operational
 *     surface ticks together).
 *   - Auth refresh handled transparently by `apiClient` (audience-aware
 *     refresh routes the 401 to /auth/login/refresh-sysadmin).
 *   - Standardised error shape — every consumer gets a plain `Error`
 *     with a `.message`, suitable for piping straight into <PageError>.
 *   - Optional Zod schema validation on the response. When provided we
 *     run `schema.parse(json)`; a parse failure surfaces as a typed
 *     `SysadminQueryParseError` so callers can distinguish a server
 *     bug from a transport failure.
 *
 * Two shapes are exported:
 *
 *   useSysadminQuery({ key, url, schema })
 *     One-shot inline hook — most convenient for a quick page.
 *
 *   createSysadminQuery({ key, url, schema })
 *     Factory that returns a hook. Use this in `lib/use-sysadmin-*.ts`
 *     modules so a single canonical hook can be shared across routes.
 *
 * Both call paths funnel into the same TanStack Query options so the
 * defaults stay in one place.
 *
 * @example one-shot
 * function GovernanceOverviewPage() {
 *   const { data, error, isLoading, refetch } = useSysadminQuery({
 *     key: ['sysadmin-governance-overview'],
 *     url: '/admin/platform/governance/overview',
 *     schema: GovernanceOverviewSchema,
 *   });
 *   if (error) return <PageError title="Failed to load" message={error.message} onRetry={refetch} />;
 *   …
 * }
 *
 * @example factory
 * export const useSysadminGovernanceOverview = createSysadminQuery({
 *   key: ['sysadmin-governance-overview'],
 *   url: '/admin/platform/governance/overview',
 *   schema: GovernanceOverviewSchema,
 * });
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { z } from 'zod';
import { apiClient } from './api-client';

/**
 * Default refresh interval for sysadmin operational data. Matches the
 * existing useSysadminServices() / useRuntimeHardware() hooks so that
 * every sysadmin surface ticks together.
 */
export const SYSADMIN_DEFAULT_REFRESH_MS = 10_000;

/**
 * Default staleTime for sysadmin queries. Half the refresh interval —
 * keeps tab-revisit snappy without re-fetching mid-cycle.
 */
export const SYSADMIN_DEFAULT_STALE_MS = 5_000;

/**
 * Thrown when the API returned a 2xx response but the body didn't
 * match the Zod schema. Callers can distinguish this from a transport
 * error via `error instanceof SysadminQueryParseError`.
 */
export class SysadminQueryParseError extends Error {
  readonly cause: unknown;
  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'SysadminQueryParseError';
    this.cause = cause;
  }
}

/**
 * Options shared by `createSysadminQuery` and `useSysadminQuery`.
 */
export interface SysadminQueryOptions<T> {
  /** Query key — must be unique per URL+params. Becomes the cache key. */
  key: readonly unknown[];
  /** Endpoint path relative to /api/v1 (apiClient prepends the base). */
  url: string;
  /** Optional Zod schema to validate the response body. */
  schema?: z.ZodType<T>;
  /**
   * Polling interval in ms. Defaults to SYSADMIN_DEFAULT_REFRESH_MS
   * (10s). Pass `false` to disable auto-refresh (used by expensive
   * verify-style endpoints).
   */
  refreshMs?: number | false;
  /**
   * staleTime — defaults to SYSADMIN_DEFAULT_STALE_MS.
   */
  staleMs?: number;
  /**
   * If false, the query is suspended until enabled flips to true. Use
   * for on-demand fetches (verify chain, kick-off jobs).
   */
  enabled?: boolean;
  /**
   * If true, fetch on window focus. Default false — sysadmin pages
   * already poll, and refocus storms are noisy in long-running ops
   * consoles.
   */
  refetchOnWindowFocus?: boolean;
}

/**
 * Run the request + optional Zod validation. Exported for tests; not
 * intended for direct call-site use.
 */
export async function _sysadminQueryFn<T>(
  url: string,
  schema?: z.ZodType<T>,
): Promise<T> {
  const json = await apiClient.get<unknown>(url);
  if (!schema) return json as T;
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    // Surface the schema path that failed so a server bug is easy to
    // diagnose without diving into the Network tab.
    const first = parsed.error.issues[0];
    const path = first?.path?.join('.') || '<root>';
    throw new SysadminQueryParseError(
      `Response from ${url} failed schema validation at '${path}': ${first?.message ?? 'unknown issue'}`,
      parsed.error,
    );
  }
  return parsed.data;
}

/**
 * Convert SysadminQueryOptions into the shape TanStack Query expects.
 */
function buildQueryConfig<T>(opts: SysadminQueryOptions<T>) {
  const refresh =
    opts.refreshMs === undefined ? SYSADMIN_DEFAULT_REFRESH_MS : opts.refreshMs;
  return {
    queryKey: opts.key,
    queryFn: () => _sysadminQueryFn<T>(opts.url, opts.schema),
    refetchInterval: refresh === false ? (false as const) : refresh,
    refetchIntervalInBackground: false,
    staleTime: opts.staleMs ?? SYSADMIN_DEFAULT_STALE_MS,
    enabled: opts.enabled,
    refetchOnWindowFocus: opts.refetchOnWindowFocus ?? false,
  };
}

/**
 * One-shot hook — runs a sysadmin query at the call site. Most
 * convenient when the URL is known statically. For shared use across
 * multiple components prefer `createSysadminQuery` below.
 */
export function useSysadminQuery<T>(
  opts: SysadminQueryOptions<T>,
): UseQueryResult<T, Error> {
  return useQuery<T, Error>(buildQueryConfig(opts));
}

/**
 * Factory that returns a hook. The hook accepts no arguments and is
 * meant to be exported from a `lib/use-sysadmin-*.ts` module so every
 * consumer points at the same cache entry.
 *
 * Returns a hook tied to the supplied URL + schema. The hook itself
 * is just `useQuery(buildQueryConfig(opts))` — pure delegation.
 */
export function createSysadminQuery<T>(
  opts: SysadminQueryOptions<T>,
): () => UseQueryResult<T, Error> {
  const config = buildQueryConfig(opts);
  return function useSysadminQueryFactoryHook() {
    return useQuery<T, Error>(config);
  };
}

// ─── Mutations ───────────────────────────────────────────────────────────

/**
 * Options for `createSysadminMutation`.
 *
 * @param TInput   The hook accepts a single argument of this shape (the
 *                 mutation variables). Pass `void` if the mutation takes
 *                 no input.
 * @param TOutput  The promise resolves to this shape.
 *
 * `url` may be a static string or a function `(input) => string` —
 * useful for resource-id-bearing paths like `/admin/platform/keys/jwt/${id}/force-retire`.
 */
export interface SysadminMutationOptions<TInput, TOutput> {
  /**
   * Endpoint path. Either a static string or a function that computes
   * the path from the input variables. The function form lets a single
   * mutation factory handle id-bearing URLs without a wrapper hook.
   */
  url: string | ((input: TInput) => string);
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * Zod schema for the body. When provided we `.parse()` the input
   * before sending — catches client-side bugs before they hit the API.
   * For DELETE without a body (the common case), pass z.void() and the
   * factory will skip the body entirely.
   */
  inputSchema?: z.ZodType<TInput>;
  /**
   * Zod schema for the response. Mirrors createSysadminQuery — surfaces
   * a typed `SysadminQueryParseError` if the server returns an
   * unexpected shape so the caller can distinguish that from a network
   * error.
   */
  outputSchema?: z.ZodType<TOutput>;
  /**
   * Query keys to invalidate after a successful mutation. Each entry is
   * passed to `queryClient.invalidateQueries({ queryKey })` so the
   * affected list/detail queries refetch immediately. Skip the post-
   * invalidate cycle by passing an empty array — the 10s default poll
   * still catches the change.
   */
  invalidates?: readonly (readonly unknown[])[];
}

/**
 * Variables passed to a mutation that requires the destructive
 * step-up reason header. Wraps the regular mutation input plus the
 * operator's `stepUpReason` string. The factory below splits the
 * reason out and surfaces it as the `X-Step-Up-Reason` request
 * header (#1131 / #1136 — D2 / Tier-1 step-up contract).
 */
export interface StepUpMutationInput<TInput> {
  /** Real mutation body. */
  input: TInput;
  /** 3-500 char reason recorded in the platform_audit_logs row. */
  stepUpReason: string;
}

/**
 * Run the mutation request + optional schema validation. Exported for
 * tests only.
 */
export async function _sysadminMutationFn<TInput, TOutput>(
  opts: SysadminMutationOptions<TInput, TOutput>,
  input: TInput,
): Promise<TOutput> {
  // Validate input first so a malformed body never reaches the network.
  let payload = input;
  if (opts.inputSchema) {
    const parsed = opts.inputSchema.safeParse(input);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const path = first?.path?.join('.') || '<root>';
      throw new SysadminQueryParseError(
        `Mutation input failed schema validation at '${path}': ${first?.message ?? 'unknown issue'}`,
        parsed.error,
      );
    }
    payload = parsed.data;
  }

  const url =
    typeof opts.url === 'function' ? opts.url(input) : opts.url;

  let response: unknown;
  switch (opts.method) {
    case 'POST':
      response = await apiClient.post<unknown>(url, payload);
      break;
    case 'PATCH':
      response = await apiClient.patch<unknown>(url, payload);
      break;
    case 'PUT':
      response = await apiClient.put<unknown>(url, payload);
      break;
    case 'DELETE':
      // apiClient.delete doesn't accept a body. The Air-Gap and Keys
      // endpoints accept an OPTIONAL `{ confirmed: true }` body to act
      // as a server-side double-check beyond the typed-confirm dialog.
      // For DELETE-with-body we round-trip via fetch with method:DELETE
      // on the underlying request helper rather than the convenience
      // method so the confirmed flag can ride along.
      if (payload !== undefined && payload !== null) {
        response = await apiClient.request<unknown>(url, {
          method: 'DELETE',
          body: JSON.stringify(payload),
        });
      } else {
        response = await apiClient.delete<unknown>(url);
      }
      break;
  }

  if (!opts.outputSchema) return response as TOutput;
  const parsed = opts.outputSchema.safeParse(response);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const path = first?.path?.join('.') || '<root>';
    throw new SysadminQueryParseError(
      `Response from ${url} failed schema validation at '${path}': ${first?.message ?? 'unknown issue'}`,
      parsed.error,
    );
  }
  return parsed.data;
}

/**
 * Factory that returns a hook backed by TanStack `useMutation`. The
 * hook accepts the standard `useMutation` shape (mutate, mutateAsync,
 * isPending, isError, error, reset, …) and is meant to be exported from
 * a `lib/use-sysadmin-*.ts` module so consumers point at a single
 * canonical mutation.
 *
 * Invalidation: after a successful mutation, every queryKey in
 * `opts.invalidates` is invalidated against the query cache, which
 * triggers an immediate refetch on subscribed queries. The factory
 * keeps the invalidation list static (closure-captured) so the cache
 * touches are predictable — runtime overrides happen in the
 * `useMutation({ onSuccess })` argument the caller passes when invoking
 * the hook.
 *
 * @example
 * export const useUpdateDeployment = createSysadminMutation({
 *   url: '/admin/platform/deployment',
 *   method: 'PATCH',
 *   inputSchema: DeploymentPatchSchema,
 *   outputSchema: DeploymentConfigSchema,
 *   invalidates: [['sysadmin-platform-deployment']],
 * });
 *
 * // In a component:
 * const update = useUpdateDeployment();
 * update.mutate({ mode: 'standalone' });
 */
export function createSysadminMutation<TInput, TOutput>(
  opts: SysadminMutationOptions<TInput, TOutput>,
): () => UseMutationResult<TOutput, Error, TInput, unknown> {
  return function useSysadminMutationFactoryHook() {
    const queryClient = useQueryClient();
    return useMutation<TOutput, Error, TInput, unknown>({
      mutationFn: (input: TInput) => _sysadminMutationFn(opts, input),
      onSuccess: () => {
        if (!opts.invalidates) return;
        for (const key of opts.invalidates) {
          queryClient.invalidateQueries({ queryKey: key as unknown[] });
        }
      },
    });
  };
}

// ─── Step-up mutation (X-Step-Up-Reason header) ─────────────────────────

const STEP_UP_REASON_MIN = 3;
const STEP_UP_REASON_MAX = 500;

/**
 * Validate the operator's step-up reason client-side before firing
 * the mutation. Same bounds as the backend's `require-fresh-totp.ts`
 * (3-500 chars, trimmed).
 */
export function validateStepUpReason(raw: string): {
  ok: boolean;
  trimmed: string;
  message?: string;
} {
  const trimmed = raw.trim();
  if (trimmed.length < STEP_UP_REASON_MIN) {
    return {
      ok: false,
      trimmed,
      message: `Reason must be at least ${STEP_UP_REASON_MIN} characters.`,
    };
  }
  if (trimmed.length > STEP_UP_REASON_MAX) {
    return {
      ok: false,
      trimmed,
      message: `Reason must be at most ${STEP_UP_REASON_MAX} characters.`,
    };
  }
  return { ok: true, trimmed };
}

/**
 * Run a step-up mutation: same shape as `_sysadminMutationFn` plus
 * the `X-Step-Up-Reason` header.
 *
 * Exported for tests.
 */
export async function _sysadminStepUpMutationFn<TInput, TOutput>(
  opts: SysadminMutationOptions<TInput, TOutput>,
  vars: StepUpMutationInput<TInput>,
): Promise<TOutput> {
  // Validate the reason before any network hop.
  const reason = validateStepUpReason(vars.stepUpReason);
  if (!reason.ok) {
    throw new SysadminQueryParseError(
      reason.message ?? 'Invalid step-up reason',
      null,
    );
  }

  // Validate the body via the input schema if present.
  let payload = vars.input;
  if (opts.inputSchema) {
    const parsed = opts.inputSchema.safeParse(vars.input);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const path = first?.path?.join('.') || '<root>';
      throw new SysadminQueryParseError(
        `Mutation input failed schema validation at '${path}': ${first?.message ?? 'unknown issue'}`,
        parsed.error,
      );
    }
    payload = parsed.data;
  }

  const url =
    typeof opts.url === 'function' ? opts.url(vars.input) : opts.url;

  const headers: Record<string, string> = {
    'X-Step-Up-Reason': reason.trimmed,
  };
  // apiClient.{post,patch,put,delete} accept an `init?: RequestInit` —
  // we ride the existing path rather than re-implementing the
  // refresh-on-401 chain here.
  let response: unknown;
  switch (opts.method) {
    case 'POST':
      response = await apiClient.post<unknown>(url, payload, { headers });
      break;
    case 'PATCH':
      response = await apiClient.patch<unknown>(url, payload, { headers });
      break;
    case 'PUT':
      response = await apiClient.put<unknown>(url, payload, { headers });
      break;
    case 'DELETE':
      if (payload !== undefined && payload !== null) {
        response = await apiClient.request<unknown>(url, {
          method: 'DELETE',
          body: JSON.stringify(payload),
          headers,
        });
      } else {
        response = await apiClient.delete<unknown>(url, { headers });
      }
      break;
  }

  if (!opts.outputSchema) return response as TOutput;
  const parsed = opts.outputSchema.safeParse(response);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const path = first?.path?.join('.') || '<root>';
    throw new SysadminQueryParseError(
      `Response from ${url} failed schema validation at '${path}': ${first?.message ?? 'unknown issue'}`,
      parsed.error,
    );
  }
  return parsed.data;
}

/**
 * Factory that returns a step-up mutation hook.
 *
 * The hook accepts `{ input, stepUpReason }` as its variables. The
 * `stepUpReason` ends up as the `X-Step-Up-Reason` header on the
 * outgoing request. The backend's `require-fresh-totp.ts` gate
 * validates the same 3-500 char bounds we enforce client-side via
 * `validateStepUpReason`.
 *
 * Usage:
 *
 *   export const useAddAllowlistEntry = createSysadminStepUpMutation({
 *     url: '/admin/platform/airgap/egress-allowlist',
 *     method: 'POST',
 *     inputSchema: AddAllowlistInputSchema,
 *     outputSchema: AllowlistEntrySchema,
 *     invalidates: [['sysadmin-platform-airgap']],
 *   });
 *
 *   const add = useAddAllowlistEntry();
 *   add.mutate({
 *     input: { hostname, port, protocol, reason, confirmed: true },
 *     stepUpReason: 'Forge code-gen needs vendor docs (audit reason)',
 *   });
 */
export function createSysadminStepUpMutation<TInput, TOutput>(
  opts: SysadminMutationOptions<TInput, TOutput>,
): () => UseMutationResult<TOutput, Error, StepUpMutationInput<TInput>, unknown> {
  return function useSysadminStepUpMutationFactoryHook() {
    const queryClient = useQueryClient();
    return useMutation<TOutput, Error, StepUpMutationInput<TInput>, unknown>({
      mutationFn: (vars: StepUpMutationInput<TInput>) =>
        _sysadminStepUpMutationFn(opts, vars),
      onSuccess: () => {
        if (!opts.invalidates) return;
        for (const key of opts.invalidates) {
          queryClient.invalidateQueries({ queryKey: key as unknown[] });
        }
      },
    });
  };
}
